import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Trash2, UploadCloud, ImageOff, Image as ImageIcon, Calendar, Activity, X, HardDrive } from "lucide-react";

/**
 * Scans tab — shows every uploaded (or auto-ingested) kiosk slip with
 * thumbnail, date, source, health score, and a one-click "Delete image" that
 * frees quota while keeping the vitals + AI report rows intact.
 *
 * Supports Family Accounts: when `dependentId` is non-null, scans are
 * scoped to that dependent instead of the signed-in patient.
 */
export function ScansTab({ onOpenUpload, dependentId = null, dependentName = null }) {
    const [scans, setScans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [storage, setStorage] = useState(null);
    const [preview, setPreview] = useState(null); // {scan_id, image_base64, mime_type, created_at, source}

    const load = async () => {
        setLoading(true);
        try {
            const params = dependentId ? { params: { dependent_id: dependentId } } : {};
            const [{ data: scansData }, { data: storageData }] = await Promise.all([
                api.get("/me/scans", params),
                api.get("/me/scans/storage"),
            ]);
            setScans(Array.isArray(scansData) ? scansData : []);
            setStorage(storageData);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to load scans");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
         
    }, [dependentId]);

    const openPreview = async (scan) => {
        if (scan.image_deleted) {
            toast.info("Image was deleted. Vitals and AI report remain available in Medical Records.");
            return;
        }
        try {
            const { data } = await api.get(`/me/scans/${scan.id}/image`);
            setPreview({
                scan_id: scan.id,
                image_base64: data.image_base64,
                mime_type: data.mime_type || scan.mime_type || "image/jpeg",
                created_at: scan.created_at,
                source: scan.uploaded_by_role,
                image_deleted: data.image_deleted,
            });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not open image");
        }
    };

    const onDelete = async (scanId) => {
        const yes = window.confirm(
            "Delete this slip image to free storage? Your vitals, Health Score and AI report for this scan will remain available — only the photo is removed."
        );
        if (!yes) return;
        try {
            await api.delete(`/me/scans/${scanId}`);
            toast.success("Image deleted. Vitals and report still available.");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
        }
    };

    const usedMB = storage ? (storage.used_bytes / (1024 * 1024)).toFixed(1) : null;
    const quotaMB = storage ? storage.quota_mb : 500;
    const pct = storage ? Math.min(100, (storage.used_bytes / storage.quota_bytes) * 100) : 0;

    const sourceLabel = (s) => {
        const src = (s.source || s.uploaded_by_role || "upload").toLowerCase();
        if (src === "push") return { label: "Kiosk device", color: "bg-emerald-50 text-emerald-700" };
        if (src === "admin") return { label: "Admin upload", color: "bg-blue-50 text-blue-700" };
        if (src === "simulate") return { label: "Simulation", color: "bg-gray-100 text-gray-600" };
        return { label: "Upload", color: "bg-orange-50 text-orange-700" };
    };

    return (
        <div className="space-y-6" data-testid="tab-scans">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display text-2xl font-semibold">
                        {dependentName ? `${dependentName}'s Scan history` : "Scan history"}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Every assessment slip you've uploaded or captured at a kiosk. Delete the image to free storage —
                        the metrics and AI report always stay in your records.
                    </p>
                </div>
                <button
                    onClick={onOpenUpload}
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="scans-tab-upload-btn"
                >
                    <UploadCloud className="w-4 h-4" /> Upload a scan
                </button>
            </div>

            {storage && (
                <div className="card-soft p-5" data-testid="scans-storage-card">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-orange-50 grid place-items-center text-[var(--medx-orange)]">
                            <HardDrive className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                            <div className="font-display text-base font-semibold">File storage</div>
                            <div className="text-xs text-gray-500">
                                {usedMB} MB used of {quotaMB} MB · shared across all your dependents
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="font-mono text-lg font-semibold">{Math.round(pct)}%</div>
                        </div>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all ${pct > 90 ? "bg-red-500" : pct > 70 ? "bg-amber-500" : "bg-emerald-500"}`}
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-center text-gray-500 py-12" data-testid="scans-loading">Loading your scans…</div>
            ) : scans.length === 0 ? (
                <div className="card-soft p-10 text-center" data-testid="scans-empty">
                    <div className="w-16 h-16 mx-auto rounded-full bg-orange-50 grid place-items-center mb-4">
                        <ImageIcon className="w-7 h-7 text-[var(--medx-orange)]" />
                    </div>
                    <div className="font-display text-2xl font-semibold mb-2">No scans yet</div>
                    <p className="text-gray-600 max-w-md mx-auto mb-6">
                        Upload a photo of your Medx AI assessment slip or walk up to a Medx AI Kiosk to start building your scan history.
                    </p>
                    <button onClick={onOpenUpload} className="btn-primary inline-flex items-center gap-2" data-testid="scans-empty-upload">
                        <UploadCloud className="w-4 h-4" /> Upload your first scan
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {scans.map((s) => {
                        const src = sourceLabel(s);
                        const vitals = s.ocr_vitals || {};
                        const health = vitals.health_score;
                        return (
                            <div
                                key={s.id}
                                className="card-soft overflow-hidden flex flex-col"
                                data-testid={`scan-card-${s.id}`}
                            >
                                <ScanThumbnail scanId={s.id} imageDeleted={s.image_deleted} onClick={() => openPreview(s)} />
                                <div className="p-4 space-y-3 flex-1 flex flex-col">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className={`text-[10px] font-mono tracking-wider px-2 py-1 rounded-full ${src.color}`}>
                                            {src.label}
                                        </span>
                                        {typeof health === "number" && health > 0 && (
                                            <span className="text-[10px] font-mono tracking-wider px-2 py-1 rounded-full bg-rose-50 text-rose-700">
                                                SCORE {health}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                        <Calendar className="w-3.5 h-3.5" />
                                        {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}
                                    </div>
                                    {vitals.blood_pressure_systolic && (
                                        <div className="flex items-center gap-2 text-xs text-gray-700">
                                            <Activity className="w-3.5 h-3.5 text-[var(--medx-red)]" />
                                            BP {vitals.blood_pressure_systolic}/{vitals.blood_pressure_diastolic} · HR {vitals.heart_rate} · SpO₂ {vitals.spo2}%
                                        </div>
                                    )}
                                    <div className="mt-auto pt-2 flex items-center justify-between">
                                        {s.image_deleted ? (
                                            <span className="text-[11px] text-gray-400 italic inline-flex items-center gap-1">
                                                <ImageOff className="w-3.5 h-3.5" /> Image deleted
                                            </span>
                                        ) : (
                                            <button
                                                onClick={() => openPreview(s)}
                                                className="text-[11px] text-[var(--medx-red)] font-semibold hover:underline"
                                                data-testid={`scan-view-${s.id}`}
                                            >
                                                View image
                                            </button>
                                        )}
                                        {!s.image_deleted && (
                                            <button
                                                onClick={() => onDelete(s.id)}
                                                className="text-[11px] inline-flex items-center gap-1 text-gray-500 hover:text-red-600 font-medium"
                                                data-testid={`scan-delete-${s.id}`}
                                            >
                                                <Trash2 className="w-3.5 h-3.5" /> Delete image
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {preview && (
                <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setPreview(null)} data-testid="scan-preview-modal">
                    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden">
                        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                            <div>
                                <h3 className="font-display text-lg font-semibold">Assessment slip</h3>
                                <p className="text-xs text-gray-500 mt-0.5">{new Date(preview.created_at).toLocaleString()}</p>
                            </div>
                            <button onClick={() => setPreview(null)} className="w-9 h-9 rounded-full hover:bg-gray-100 grid place-items-center text-gray-500" aria-label="Close" data-testid="scan-preview-close">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-5 max-h-[75vh] overflow-auto bg-gray-50">
                            {preview.image_base64 ? (
                                <img
                                    src={`data:${preview.mime_type};base64,${preview.image_base64}`}
                                    alt="Assessment slip"
                                    className="max-w-full mx-auto rounded-lg shadow"
                                />
                            ) : (
                                <div className="text-center py-20 text-gray-400">
                                    <ImageOff className="w-12 h-12 mx-auto mb-3" />
                                    <div>Image has been deleted.</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function ScanThumbnail({ scanId, imageDeleted, onClick }) {
    const [thumb, setThumb] = useState(null);
    const [loading, setLoading] = useState(!imageDeleted);
    useEffect(() => {
        let cancelled = false;
        if (imageDeleted) { setLoading(false); return; }
        setLoading(true);
        api.get(`/me/scans/${scanId}/image`).then(({ data }) => {
            if (cancelled) return;
            if (data.image_deleted || !data.image_base64) setThumb(null);
            else setThumb(`data:${data.mime_type};base64,${data.image_base64}`);
        }).catch(() => {
            if (!cancelled) setThumb(null);
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [scanId, imageDeleted]);

    return (
        <button
            type="button"
            onClick={onClick}
            className="relative aspect-[4/3] bg-gray-50 border-b border-gray-100 overflow-hidden group"
            data-testid={`scan-thumb-${scanId}`}
            disabled={imageDeleted}
        >
            {imageDeleted ? (
                <div className="w-full h-full grid place-items-center text-gray-400">
                    <div className="text-center">
                        <ImageOff className="w-10 h-10 mx-auto mb-2" />
                        <div className="text-xs font-medium">Image deleted</div>
                    </div>
                </div>
            ) : loading ? (
                <div className="w-full h-full grid place-items-center">
                    <div className="w-6 h-6 border-2 border-[var(--medx-orange)] border-t-transparent rounded-full animate-spin" />
                </div>
            ) : thumb ? (
                <img src={thumb} alt="Scan slip" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
            ) : (
                <div className="w-full h-full grid place-items-center text-gray-400">
                    <ImageIcon className="w-10 h-10" />
                </div>
            )}
        </button>
    );
}
